import { useCallback, useEffect, useState } from 'react'
import { fetchMediaBlob } from '@src/services/media'

// React side of services/media: a protected file read into a Blob, and an
// object URL of a Blob. Shared by the file viewers (FileViewer, the Bulk
// Enquiry previews), so each of them fetches, types and revokes the same way.

/** An object URL of `blob` (a Blob or a File), revoked when it changes or the component goes; null without one. */
export function useObjectUrl(blob) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    if (!blob) {
      setUrl(null)
      return undefined
    }
    const created = URL.createObjectURL(blob)
    setUrl(created)
    return () => URL.revokeObjectURL(created)
  }, [blob])
  return url
}

/**
 * A protected media file, fetched with the Bearer (services/media
 * fetchMediaBlob: the API origin only), as it arrived:
 * `{ status: 'idle' | 'loading' | 'ready' | 'error', blob, reload }`. The blob
 * keeps the proxy's type: type it with safeTypedBlob before rendering it as a
 * document (a new tab, an <iframe>). `path` null reads nothing.
 */
export function useMediaBlob(path) {
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({ status: 'idle', blob: null })
  useEffect(() => {
    if (!path) {
      setState({ status: 'idle', blob: null })
      return undefined
    }
    let active = true
    const controller = new AbortController()
    setState({ status: 'loading', blob: null })
    fetchMediaBlob(path, controller.signal)
      .then((blob) => {
        if (!active) return
        setState(blob ? { status: 'ready', blob } : { status: 'error', blob: null })
      })
      .catch((err) => {
        if (active && err?.name !== 'AbortError') setState({ status: 'error', blob: null })
      })
    return () => {
      active = false
      controller.abort()
    }
  }, [path, attempt])
  const reload = useCallback(() => setAttempt((n) => n + 1), [])
  return { ...state, reload }
}
